import React from 'react'

import { get, has } from 'lodash'
import moment from 'moment'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { getMeasurementList } from 'src/store/modules/common/thunks'
import {
  setMeasurementActiveTab,
  setMeasurementChartInfo,
  setMeasurementDashboardList,
  setMeasurementList,
  setMeasurementPatientList,
  setMeasurementPatientListAssigned,
  setMeasurementPatientReadingAnswer,
  setMeasurementPatientReadingList,
  setMeasurementPatientValue,
  setMeasurementValue
} from 'src/store/modules/patient/measurements'

import ErrorMessages from 'src/components/ErrorMessages'
import {
  DATE_TIME_FORMAT_FOR_SERVER,
  GET_PATIENT_RPM_REPORT_SINGLE,
  MEASUREMENT,
  MEASUREMENT_ALERT_STATUS_DECISION,
  MEASUREMENT_DASHBOARD,
  MEASUREMENT_PATIENT,
  MEASUREMENT_PATIENT_ANSWER_SUCCESS_SAVE_MESSAGE,
  MEASUREMENT_PATIENT_ASSIGNED,
  MEASUREMENT_PATIENT_COPY,
  MEASUREMENT_PATIENT_CREATE_READING,
  MEASUREMENT_PATIENT_EXPORT_READING_REPORT,
  MEASUREMENT_PATIENT_GET_POST,
  MEASUREMENT_PATIENT_GET_VITAL_GRAPH,
  MEASUREMENT_PATIENT_LAST_ANSWERS,
  MEASUREMENT_PATIENT_PATIENT_TREND,
  MEASUREMENT_PATIENT_READING_ANSWER,
  MEASUREMENT_PATIENT_READING_ANSWERS,
  MEASUREMENT_PATIENT_READING_ANSWER_NO_DATA_POST,
  MEASUREMENT_PATIENT_READING_ANSWER_POST,
  MEASUREMENT_PATIENT_READING_MESSAGE,
  MEASUREMENT_PATIENT_SUCCESS_SAVE_MESSAGE,
  MEASUREMENT_PATIENT_SYNC_DEVICE,
  MEASUREMENT_READING,
  MEASUREMENT_TAB_LIST
} from 'src/constants'
import { getMeasurementRule } from 'src/scenes/Admin/MeasurementRulesManagement/api'
import { showInfoModal, showSuccessModal } from 'src/services/dialogs'
import { exportReport } from 'src/utils/Functions'

export const fetchPatientMeasurementList =
  (config = {}) =>
  async (dispatch) => {
    const items = await getMeasurementList(config)

    dispatch(setMeasurementList(items))
  }

export const fetchMeasurementValue =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items } = await httpClient.get({
        pathname: MEASUREMENT,
        params,
        query
      })
      const item = get(items, [0], {})

      dispatch(setMeasurementValue(item))

      return item
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const fetchMeasurementDashboardList =
  ({ query = {} }) =>
  async (dispatch) => {
    try {
      const { items = [] } = await httpClient.request({
        method: 'get',
        url: {
          pathname: MEASUREMENT_DASHBOARD,
          query
        }
      })

      dispatch(setMeasurementDashboardList(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMeasurementPatientList =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.get({
        pathname: MEASUREMENT_PATIENT_GET_POST,
        params,
        query
      })

      dispatch(setMeasurementPatientList(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMeasurementPatientValue =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [] } = await httpClient.get({
        pathname: MEASUREMENT_PATIENT_GET_POST,
        params,
        query
      })
      const item = get(items, [0], {})

      dispatch(setMeasurementPatientValue(item))
    } catch (err) {
      catchError(err)
    }
  }

export const saveMeasurementPatientValue =
  (config = {}, patientUuid, history) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.post(
        {
          pathname: MEASUREMENT_PATIENT_GET_POST,
          params
        },
        payload
      )

      showSuccessModal({
        content: MEASUREMENT_PATIENT_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false,
        onOk: () => {
          dispatch(setMeasurementActiveTab(MEASUREMENT_TAB_LIST[2]))

          return history.push(`/patients/${patientUuid}/measurements`)
        }
      })
    } catch (err) {
      catchError(
        {
          ...err,
          message: <ErrorMessages errorObj={err} />
        },
        {
          width: 400,
          ignoredValuesList: ['frequency.reminders']
        }
      )
    }
  }

export const updateMeasurementPatientValue =
  (config = {}, patientUuid = '', history, isModal, onCancel) =>
  async (dispatch) => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.patch(
        {
          pathname: MEASUREMENT_PATIENT,
          params
        },
        payload
      )

      showSuccessModal({
        content: MEASUREMENT_PATIENT_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false,
        onOk: () => {
          if (isModal) {
            onCancel()
          }

          if (patientUuid && history) {
            dispatch(setMeasurementActiveTab(MEASUREMENT_TAB_LIST[2]))

            return history.push(`/patients/${patientUuid}/measurements`)
          }

          return false
        }
      })
    } catch (err) {
      catchError(
        {
          ...err,
          message: <ErrorMessages errorObj={err} />
        },
        {
          width: 400,
          ignoredValuesList: ['frequency.reminders']
        }
      )
    }
  }

export const deleteMeasurementPatientValue =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.delete({
        pathname: MEASUREMENT_PATIENT,
        params
      })
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMeasurementPatientReadingAnswersList =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [], meta = {} } = await httpClient.get({
        pathname: MEASUREMENT_PATIENT_READING_ANSWERS,
        params,
        query
      })

      dispatch(setMeasurementPatientReadingList(items))

      return meta
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const apiFetchMeasurementPatientReadingAnswersList = async ({
  params = {},
  query = {}
} = {}) => {
  try {
    return await httpClient.get({
      pathname: MEASUREMENT_PATIENT_READING_ANSWERS,
      params,
      query
    })
  } catch (err) {
    catchError(err)

    return { items: [], meta: {} }
  }
}

export const createMeasurementPatientReading =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.post(
        {
          pathname: MEASUREMENT_PATIENT_CREATE_READING,
          params
        },
        payload
      )

      showSuccessModal({
        content: MEASUREMENT_PATIENT_READING_MESSAGE,
        centered: true,
        keyboard: false
      })
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMeasurementPatientReadingAnswer =
  (config = {}, answer = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      let measurementPatientReadingModalAnswer = await httpClient.get({
        pathname: MEASUREMENT_PATIENT_READING_ANSWER,
        params,
        query
      })
      measurementPatientReadingModalAnswer = {
        ...answer,
        ...measurementPatientReadingModalAnswer
      }

      dispatch(
        setMeasurementPatientReadingAnswer(measurementPatientReadingModalAnswer)
      )
    } catch (err) {
      catchError(err)
    }
  }

export const saveMeasurementPatientReadingAnswer =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      if (has(params, 'readingUuid')) {
        await httpClient.post(
          {
            pathname: MEASUREMENT_PATIENT_READING_ANSWER_POST,
            params
          },
          payload
        )
      } else {
        await httpClient.post(
          {
            pathname: MEASUREMENT_PATIENT_READING_ANSWER_NO_DATA_POST,
            params
          },
          payload
        )
      }

      showSuccessModal({
        content: MEASUREMENT_PATIENT_ANSWER_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const updateMeasurementPatientReadingAnswer =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.patch(
        {
          pathname: MEASUREMENT_PATIENT_READING_ANSWER,
          params
        },
        payload
      )

      showSuccessModal({
        content: MEASUREMENT_PATIENT_ANSWER_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const deleteMeasurementPatientReadingAnswer =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.delete({
        pathname: MEASUREMENT_PATIENT_READING_ANSWER,
        params
      })
    } catch (err) {
      catchError(err)
    }
  }

export const deleteMeasurementPatientReading =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.delete({
        pathname: MEASUREMENT_READING,
        params
      })
    } catch (err) {
      catchError(err)
    }
  }

export const fetchMeasurementPatientAssigned =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const items = await httpClient.get({
        pathname: MEASUREMENT_PATIENT_ASSIGNED,
        params,
        query
      })

      dispatch(setMeasurementPatientListAssigned(items))
    } catch (err) {
      catchError(err)
    }
  }

export const exportMeasurementPatientReadingReportPdf =
  (config = {}) =>
  async () => {
    const updConfig = {
      pathname: GET_PATIENT_RPM_REPORT_SINGLE,
      ...config
    }
    const fileNameDefault = `patient_readings_answers_${moment().format(
      DATE_TIME_FORMAT_FOR_SERVER
    )}.pdf`

    await exportReport(updConfig, fileNameDefault)
  }

export const exportMeasurementPatientReadingReportCsv =
  (config = {}) =>
  async () => {
    const updConfig = {
      pathname: MEASUREMENT_PATIENT_EXPORT_READING_REPORT,
      ...config
    }
    const fileNameDefault = `patient_readings_answers_${moment().format(
      DATE_TIME_FORMAT_FOR_SERVER
    )}.csv`

    await exportReport(updConfig, fileNameDefault)
  }

export const fetchMeasurementPatientVitalGraph =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config
    if (!params.patientUuid) {
      return
    }

    try {
      const measurementChartInfo = await httpClient.get({
        pathname: MEASUREMENT_PATIENT_GET_VITAL_GRAPH,
        params,
        query
      })

      dispatch(setMeasurementChartInfo(measurementChartInfo))
    } catch (err) {
      catchError(err)
    }
  }

export const copyMeasurementPatient =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      const { isCopiedWithDefaultRules } = await httpClient.post({
        pathname: MEASUREMENT_PATIENT_COPY,
        params
      })

      if (isCopiedWithDefaultRules) {
        showInfoModal({
          content:
            'Unable to copy old measurement rules. Default measurement rules have been applied.',
          centered: true,
          keyboard: false
        })
      }

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const getMeasurementPatientAlertStatusDecision =
  (config = {}) =>
  async () => {
    const {
      pathname = MEASUREMENT_ALERT_STATUS_DECISION,
      params = {},
      payload = {}
    } = config

    try {
      const { askConfirmQuestion = false } = await httpClient.request({
        method: 'post',
        url: {
          pathname,
          params
        },
        payload
      })

      return { askConfirmQuestion, hasError: false }
    } catch (err) {
      catchError(err)

      return { askConfirmQuestion: false, hasError: true }
    }
  }

export const getPatientLastAnswers = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: MEASUREMENT_PATIENT_LAST_ANSWERS,
        params,
        query
      }
    })
  } catch (err) {
    catchError(err)

    return false
  }
}

export const getPatientTrend = async (config = {}) => {
  const { params = {}, query = {} } = config

  try {
    return await httpClient.request({
      method: 'get',
      url: {
        pathname: MEASUREMENT_PATIENT_PATIENT_TREND,
        params,
        query
      }
    })
  } catch (err) {
    catchError(err)

    return false
  }
}

export const syncPatientDevice =
  (config = {}) =>
  async () => {
    const { params = {}, query = {} } = config

    try {
      await httpClient.request({
        method: 'get',
        url: {
          pathname: MEASUREMENT_PATIENT_SYNC_DEVICE,
          params,
          query
        }
      })
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const fetchMeasurementValueByUuid =
  (config = {}) =>
  async (dispatch) => {
    try {
      const response = await getMeasurementRule(config)

      dispatch(setMeasurementValue(response))

      return response
    } catch (err) {
      catchError(err)

      return false
    }
  }
